import { useEffect, useState } from 'react'
import { ActionButton } from './ActionButton.js'
import { notify, notifyError } from './Toasts.js'
import { api } from '../net/apiClient.js'
import { db } from '../net/localDb.js'
import { uuidv7 } from '../net/ids.js'

/**
 * Der Zugang zum Handelsregister, in den Einstellungen des Betriebs.
 *
 * Er steht hier und nicht in einer Servervariable, weil Raporto mandantenfähig
 * ist: der Bund vergibt den Zugang zum Zentralen Firmenindex pro Firma, und
 * eine gemeinsame Kennung für alle Mandanten wäre ein Verstoss gegen seine
 * Nutzungsbedingungen. Jeder Betrieb meldet ihn selbst an und trägt ihn selbst
 * ein.
 *
 * Drei Dinge, die hier Absicht sind:
 *
 *   Das Zugangswort steht nach dem Speichern nicht mehr da – auch nicht
 *   verkürzt, auch nicht als Punktereihe mit der richtigen Länge. Der
 *   Bildschirm sagt, dass eines hinterlegt ist, und mehr weiss er auch nicht:
 *   die Schnittstelle gibt es nicht zurück.
 *
 *   Ein eigener Speicherweg, getrennt vom übrigen Einstellungsformular. Würde
 *   es mitgespeichert, überschriebe jedes Ändern der Firmenadresse das
 *   hinterlegte Wort mit dem leeren Feld, das der Bildschirm anzeigt.
 *
 *   Geprüft wird beim Bund und nicht hier. Ein «Verbindung testen» wäre nett
 *   und würde eine Zusage machen, die wir nicht halten können – der Dienst
 *   kann in dem Moment antworten und morgen nicht mehr. Was falsch ist, sagt
 *   die erste Suche, und zwar dort, wo jemand damit arbeitet.
 */
export function ZefixEinstellung(): JSX.Element {
  const [benutzer, setBenutzer] = useState('')
  const [wort, setWort] = useState('')
  const [hinterlegt, setHinterlegt] = useState(false)
  const [geladen, setGeladen] = useState(false)

  useEffect(() => {
    void db.settings.get('tenant').then((row) => {
      if (row) {
        const r = row as Record<string, unknown>
        setBenutzer(String(r.zefixUser ?? ''))
        setHinterlegt(r.zefixZugangswortGesetzt === true)
      }
      setGeladen(true)
    })
  }, [])

  return (
    <div className="o-card">
      <div className="o-card-head">
        <h3>Handelsregister</h3>
        <span className="o-soft">
          {geladen && hinterlegt ? 'Eingerichtet' : 'Noch nicht eingerichtet'}
        </span>
      </div>
      <div className="o-pad">
        <p className="o-soft" style={{ marginTop: 0 }}>
          Mit einem Zugang zum Zentralen Firmenindex (Zefix) übernimmst du beim Anlegen eines Kunden
          Firmenname, Adresse und UID direkt aus dem Handelsregister, statt sie abzuschreiben.
        </p>
        <p className="o-soft">
          Den Zugang beantragst du selbst beim Bund, auf{' '}
          <span className="o-mono">zefix.admin.ch</span> unter «Zefix Public REST API». Er gehört
          deinem Betrieb und wird von keinem anderen Raporto-Kunden mitbenutzt. Über Bedingungen und
          Kosten entscheidet der Bund.
        </p>

        <div className="o-grid o-grid-2" style={{ marginTop: 16 }}>
          <div className="o-field">
            <label htmlFor="zefix-benutzer">Benutzername</label>
            <input
              id="zefix-benutzer"
              value={benutzer}
              autoComplete="off"
              onChange={(e) => setBenutzer(e.target.value)}
            />
          </div>
          <div className="o-field">
            <label htmlFor="zefix-wort">Zugangswort</label>
            <input
              id="zefix-wort"
              type="password"
              value={wort}
              autoComplete="new-password"
              placeholder={hinterlegt ? 'hinterlegt – zum Ändern neu eintippen' : ''}
              onChange={(e) => setWort(e.target.value)}
            />
            <span className="hint">
              Wird verschlüsselt gespeichert und danach nie wieder angezeigt.
            </span>
          </div>
        </div>

        <div className="o-aktionen" style={{ marginTop: 16 }}>
          <ActionButton
            id="btn-zefix-speichern"
            labels={{ idle: 'Zugang speichern', busy: 'Speichert …', success: 'Gespeichert' }}
            onAction={async () => {
              if (!benutzer.trim() || !wort.trim()) {
                notify.warn('Benutzername und Zugangswort braucht es beide.')
                return
              }
              try {
                await api.zefixZugangSetzen(benutzer.trim(), wort, uuidv7())
                /*
                  Das Wort wird sofort aus dem Bildschirm genommen. Es hat auf
                  einem Formular nichts verloren, das jemand offen stehen
                  lässt, während er Kaffee holt.
                */
                setWort('')
                setHinterlegt(true)
                notify.ok(
                  'Der Zugang ist hinterlegt. Die Suche steht ab sofort in der Kundenanlage.'
                )
              } catch (e) {
                notifyError(e)
              }
            }}
          />
          {hinterlegt && (
            <ActionButton
              id="btn-zefix-entfernen"
              variant="secondary"
              labels={{ idle: 'Zugang entfernen', busy: 'Entfernt …', success: 'Entfernt' }}
              onAction={async () => {
                try {
                  await api.zefixZugangEntfernen(uuidv7())
                  setBenutzer('')
                  setWort('')
                  setHinterlegt(false)
                  notify.ok('Der Zugang ist entfernt. Kunden lassen sich weiter von Hand erfassen.')
                } catch (e) {
                  notifyError(e)
                }
              }}
            />
          )}
        </div>
      </div>
    </div>
  )
}
